import { Typography, Box, TypographyTypeMap } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ReactElement, ReactNode, useMemo } from 'react';
import { transformTextForKey } from 'helpers/text-transformer';
import { displayWithFallback } from 'helpers/common';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    maxWidth: '100%',
  },
  item: {
    marginTop: theme.spacing(2),
  },
  ellipsisOverFlow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  breakLineOverFlow: {
    lineBreak: 'anywhere',
  },
  hiddenOverFlow: {
    overflow: 'hidden',
  },
}));

export type ReadOnlyItemType = {
  key: string;
  title: ReactNode;
  value: number | string | string[] | ReactElement | undefined;
};

export type TTextOverFlowType = 'ellipsis' | 'breakLine' | 'hidden';

interface TParams {
  items: ReadOnlyItemType[];
  overFlowType?: TTextOverFlowType;
  titleVariant?: TypographyTypeMap['props']['variant'];
}

export const ReadOnlyForm = ({
  items,
  titleVariant,
  overFlowType,
}: TParams) => {
  const classes = useStyles();
  const textOverFlowClass = useMemo(() => {
    switch (overFlowType) {
      case 'ellipsis':
        return classes.ellipsisOverFlow;
      case 'hidden':
        return classes.hiddenOverFlow;
      case 'breakLine':
        return classes.breakLineOverFlow;
      default:
        return undefined;
    }
  }, [classes, overFlowType]);

  return (
    <Box className={classes.root}>
      {items.map((item) => (
        <Box className={classes.item} key={item.key}>
          <Typography
            variant={titleVariant || 'body2'}
            color="text.secondary"
            data-testid={`field-label-${transformTextForKey(item.key)}`}
          >
            {item.title}
          </Typography>
          {Array.isArray(item.value) ? (
            item.value.map((val, index) => (
              <Typography
                variant="body2B"
                component="h6"
                // eslint-disable-next-line react/no-array-index-key
                key={`${item.title}-${index}`}
                data-testid={`field-label-${transformTextForKey(
                  item.key,
                )}-${index}`}
                className={textOverFlowClass}
              >
                {val}
              </Typography>
            ))
          ) : (
            <Typography
              variant="body2B"
              component="h6"
              data-testid={`field-value-${transformTextForKey(item.key)}`}
              className={textOverFlowClass}
            >
              {displayWithFallback(item.value)}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};
